import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services';
import { JurisdictionService } from 'src/app/services/jurisdiction.service';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import { DeleteDialog, IDeleteDialogData } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { Jurisdiction } from 'src/app/models/Jurisdiction.model';
import { JurisdictionDialogComponent } from 'src/app/modals/jurisdiction-dialog/jurisdiction-dialog.component';
import { map, Observable, tap } from 'rxjs';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { OverviewConfig } from 'src/app/shared/grid/core-grid/overviewConfig.model';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';
import { JurisdictionDetailsPopupComponent } from 'src/app/common-components/jurisdiction-details-popup/jurisdiction-details-popup.component';
import { ChildColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { JurisdictionOrgLevelService } from 'src/app/services/jurisdiction-org-level.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { TranslateService } from '@ngx-translate/core';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';

@Component({
  standalone: false,
  selector: 'app-jurisdictions-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './jurisdictions-list.component.scss'],
})
export class JurisdictionsListComponent extends CoreGridComponent<Jurisdiction> implements OnInit, AfterViewInit {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

  totalJurisdiction: number = 0;
  jurisdictionsMissingAgents: number = 0;
  organizationLevels: Lookup[] = [];

  constructor(
    protected jurisdictionService: JurisdictionService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    private agentJurisdictionService: AgentJurisdictionService,
    private organizationLevelService: OrganizationLevelService,
    private jurisdictionOrgLevelService: JurisdictionOrgLevelService,
    gridViewService: GridViewService,
    private translateService: TranslateService,
    gridSettingsService: GridSettingsService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService,
      dialog,
      commonService,
      authService,
      gridViewService,
      gridSettingsService
    );
    this.gridService = jurisdictionService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.Jurisdiction;
  }

  // Add To My Site and Remove write GLOBAL_Jurisdiction_OrgLevel, and PermissionCheckInterceptor
  // authorizes each independently against that object (not GLOBAL_Jurisdiction), so gate them on
  // the join entity's own Create/Delete flag rather than the parent grid's canCreate/canDelete.
  protected get canAddToMySite(): boolean {
    return this.permissionService.hasPermissionFlag(RolePermissionObjects.JurisdictionOrgLevel, gridToPermissionFlags[GridPermissions.Create]);
  }

  protected get canRemoveOrgLevel(): boolean {
    return this.permissionService.hasPermissionFlag(RolePermissionObjects.JurisdictionOrgLevel, gridToPermissionFlags[GridPermissions.Delete]);
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  override initializeProperties(): void {
    this.addEntityTitle = 'Jurisdiction';
    this.searchPlaceholder = 'Search Jurisdictions';
  }

  override initializeColumns(): void {
    this.additionalActions = [];
    this.columnConfigs = [
      {
        name: nameOf<Jurisdiction>(x => x.name),
        displayName: 'Jurisdiction Name',
        cssClass: () => 'left-aligned cursor-pointer',
        headerCssClass: () => 'left-aligned',
        onClick: (row: Jurisdiction) => {
          this.showJurisdictionDetails(row);
        }
      },
      {
        name: nameOf<Jurisdiction>(x => x.addedToMySite),
        displayName: 'Add To My Site',
        isDisplayNameConfigurable: false,
        cellTemplate: () => '',
        children: (row: Jurisdiction) => {
          if (!row?.addedToMySite) {
            if (!this.canAddToMySite) {
              return [];
            }
            return [
              new ChildColumnConfig<Jurisdiction>({
                onClick: jurisdiction => this.addToSite(jurisdiction.id),
                cssClass: () => 'jurisdiction-btn main-btn',
                cellTemplate: () => this.translateService.instant('Add To My Site')
              }),
            ]
          }
          // The association is hierarchy-wide (AddedToMySiteGridService) but Remove deletes at
          // exactly the selected org level. When it's held at a descendant only, Remove would
          // silently no-op, so route to the Jurisdiction's details view instead of offering Remove.
          if (!row.addedAtSelectedOrgLevel) {
            return [
              new ChildColumnConfig<Jurisdiction>({
                onClick: jurisdiction => this.showJurisdictionDetails(jurisdiction),
                cssClass: () => 'main-btn jurisdiction-added-btn jurisdiction-added-btn-linked',
                cellTemplate: () => this.translateService.instant('Added'),
              }),
            ]
          }

          const children: ChildColumnConfig<Jurisdiction>[] = [
            new ChildColumnConfig<Jurisdiction>({
              cssClass: () => 'main-btn jurisdiction-added-btn',
              cellTemplate: () => this.translateService.instant('Added'),
              readonly: () => true
            }),
          ];
          if (this.canRemoveOrgLevel) {
            children.push(
              new ChildColumnConfig<Jurisdiction>({
                onClick: jurisdiction => this.openRemoveFromSiteDialog(jurisdiction.id),
                cssClass: () => 'jurisdiction-btn jurisdiction-remove-btn main-btn ml-2',
                cellTemplate: () => this.translateService.instant('Remove')
              }),
            );
          }
          return children;
        }
      },
      { name: 'action', displayName: 'Actions' }
    ];
  }

  showJurisdictionDetails(row: Jurisdiction): void {
    this.openFlyoutInternal(JurisdictionDetailsPopupComponent, row.id, '80vw', true);
  }

  addToSite(id: string) {
    this.jurisdictionOrgLevelService.create({ jurisdictionId: id, organizationLevelId: this.selectedOrg?.id })
      .subscribe({
        next: () => {
          this.loadComponentData();
          this.toastService.showSuccess('Jurisdiction assigned to Organization Level successfully', true);
        },
        error: this.handleError
      });
  }

  openRemoveFromSiteDialog(id: string) {
    const data: IDeleteDialogData = {
      id: id,
      formDeletes: true,
      customDelete: () => this.jurisdictionOrgLevelService.delete(id, this.selectedOrg.id),
      title: this.translateService.instant('jurisdiction from Site'),
      service: this.jurisdictionOrgLevelService,
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: data,
      width: '440px',
      disableClose: true
    });
    this.handleDialogClose(dialogRef);
  }

  private handleError = (error: any) => {
    this.toastService.showError(error);
  };

  get hasOrgFilter() {
    return true;
  }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<JurisdictionsListComponent>, null);
    super.ngOnInit();
    this.organizationLevelService.getAll().subscribe(orgs => this.organizationLevels = orgs);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [
      new OverviewConfig({
        title: this.translateService.instant('TOTAL JURISDICTIONS'),
        value: this.totalJurisdiction,
        onClick: () => this.showAllJurisdictions()
      }),
      new OverviewConfig({
        title: this.translateService.instant('JURISDICTIONS MISSING AGENTS'),
        value: this.jurisdictionsMissingAgents,
        onClick: () => this.showAllJurisdictions()
      }),
    ]
  }

  showAllJurisdictions() { /* yet to be implemented */ }

  override initializeTabs() {
    return segsRouteConfig;
  }

  override getOverviewData(): Observable<void> {
    return this.agentJurisdictionService.getJurisdictionsOverviewData(this.selectedOrg.id)
      .pipe(tap(res => {
        this.totalJurisdiction = res.jurisdictionsCount;
        this.jurisdictionsMissingAgents = res.jurisdictionsMissingAgentCount;
      }, map(() => undefined))) as any;
  }

  override openEntityDialog(jurisdictionId?: string): void {
    const jurisdictionModel: IFormDialogData = {
      id: jurisdictionId
    };
    this.openDialog(JurisdictionDialogComponent, jurisdictionModel);
  }

  override openEntityDeleteDialog(jurisdiction: Jurisdiction): void {
    this.openDeleteDialog(DeleteDialog, jurisdiction, this.jurisdictionService, 'Jurisdiction');
  }
}
